import { io, type Socket } from 'socket.io-client';
import type { Message } from './types';
import { env } from '@/lib/env';

/**
 * Live transport for the dashboard. The server pushes changes instead of the screens polling REST:
 * - `tickets:changed` — something in this workspace changed (new ticket, reply, status…); lists and
 *   counts refetch once.
 * - `message:new` / `ticket:updated` — for a ticket this socket joined (`ticket:join`); the open
 *   conversation appends the message straight into its cache.
 * Auth is the same HTTP-only access cookie the REST API uses.
 */

export type TicketChangeReason = 'created' | 'updated' | 'message';
export type AssistantState = 'thinking' | 'idle';

interface JoinAck {
  ok: boolean;
  error?: { code: string; message: string };
}

interface ServerToClientEvents {
  'message:new': (event: { ticketId: string; message: Message }) => void;
  'ticket:updated': (event: { ticketId: string; changes: string[] }) => void;
  'tickets:changed': (event: { ticketId: string; reason: TicketChangeReason }) => void;
  /** The AI assistant is preparing a reply on a joined ticket (`thinking`) or done (`idle`). */
  'assistant:status': (event: { ticketId: string; state: AssistantState; agentName: string | null }) => void;
}

interface ClientToServerEvents {
  'ticket:join': (payload: { ticketId: string }, ack?: (res: JoinAck) => void) => void;
  'ticket:leave': (payload: { ticketId: string }) => void;
}

export type AgentSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

/** `workspaceId` only matters for super admins (others are pinned to their own workspace). */
export function openAgentSocket(workspaceId: string | null): AgentSocket {
  return io(env.socketUrl, {
    path: '/socket.io',
    withCredentials: true,
    auth: workspaceId ? { workspaceId } : {},
  });
}
